import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useBatchMode } from "../contexts/BatchModeContext";
import BatchModeToggle from "../components/BatchModeToggle";
import ImageLightbox from "../components/ImageLightbox";
import StockBadge from "../components/StockBadge";
import { motion } from "framer-motion";
import { Star, Minus, Plus, ArrowLeft, Check, Maximize2 } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [selectedKey, setSelectedKey] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const { add } = useCart();
  const { user, openAuth } = useAuth();
  const { mode } = useBatchMode();

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/products/${id}`).then((r) => r.data),
      api.get(`/products/${id}/reviews`).then((r) => r.data || []),
    ])
      .then(([p, rv]) => {
        if (!alive) return;
        setProduct(p);
        setReviews(rv);
      })
      .catch(() => navigate("/", { replace: true }));
    return () => { alive = false; };
  }, [id, navigate]);

  // Packages available for the current mode
  const packages = useMemo(() => {
    if (!product) return [];
    return mode === "wholesale"
      ? product.wholesale_packages || []
      : product.small_batch_packages || [];
  }, [product, mode]);

  // Default selection: first package if any
  useEffect(() => {
    if (packages.length > 0) {
      setSelectedKey(packages[0].key);
    } else {
      setSelectedKey(null);
    }
  }, [packages]);

  // Reset qty on mode/package change to min
  useEffect(() => {
    if (!product) return;
    const selected = packages.find((p) => p.key === selectedKey);
    let min = 1;
    if (selected) min = selected.min_qty || 1;
    else if (mode === "wholesale") min = product.wholesale_min_qty || 5;
    setQty(Math.max(min, 1));
  }, [mode, selectedKey, product, packages]);

  if (!product) {
    return <div className="pt-32 text-center text-gray-500 font-mono-alt tracking-widest">LOADING...</div>;
  }

  const wholesaleAvailable =
    (product.wholesale_packages && product.wholesale_packages.length > 0) ||
    product.wholesale_price != null;
  const soldOut = product.stock_status === "out_of_stock";
  const selected = packages.find((p) => p.key === selectedKey);
  let priceContext = mode === "wholesale" ? "Wholesale" : "Small batch";
  if (selected) priceContext = selected.label;
  let reviewBtnLabel = user ? "Post review" : "Sign in to review";
  if (posting) reviewBtnLabel = "...";

  let unitPrice;
  let minQty;
  let packageLabel = null;
  if (selected) {
    unitPrice = selected.price;
    minQty = selected.min_qty || 1;
    packageLabel = selected.label;
  } else if (mode === "wholesale" && wholesaleAvailable) {
    unitPrice = product.wholesale_price;
    minQty = product.wholesale_min_qty || 5;
  } else {
    unitPrice = product.price;
    minQty = 1;
  }

  const total = (unitPrice || 0) * qty;
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  const handleAdd = () => {
    if (soldOut) return;
    add(
      product,
      {
        batchMode: mode === "wholesale" && wholesaleAvailable ? "wholesale" : "small",
        packageKey: selected ? selected.key : null,
        packageLabel,
        unitPrice,
        minQty,
      },
      qty,
    );
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      openAuth();
      return;
    }
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/products/${id}/reviews`, {
        rating, comment: comment.trim(),
      });
      setReviews([data, ...reviews]);
      setComment("");
      setRating(5);
      toast.success("Review posted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post");
    }
    setPosting(false);
  };

  return (
    <div className="pt-24 md:pt-28" data-testid="product-detail-page">
      <section className="px-6 md:px-12 lg:px-24 py-10 md:py-16">
        <Link to={`/shop/${product.category}`} className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-xs uppercase tracking-[0.3em] mb-8">
          <ArrowLeft size={14} /> Back to {product.category}
        </Link>
        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="relative border border-white/10 overflow-hidden bg-black aspect-square">
            <img src={resolveImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
            {product.image && (
              <button
                type="button"
                onClick={() => setZoomOpen(true)}
                aria-label="View full-size image"
                data-testid="detail-zoom-btn"
                className="absolute bottom-3 right-3 w-12 h-12 flex items-center justify-center rounded-full bg-black/70 backdrop-blur border border-white/25 text-white hover:bg-[var(--hk-orange)] hover:text-black active:scale-95 transition-all shadow-[0_4px_18px_rgba(0,0,0,0.55)] touch-manipulation"
              >
                <Maximize2 size={18} />
              </button>
            )}
          </motion.div>
          <div>
            <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)] uppercase mb-4 flex items-center flex-wrap gap-3">
              <span>/ {product.category}{product.strain ? ` · ${product.strain}` : ""}</span>
              <StockBadge status={product.stock_status} productId={product.id} className="tracking-widest" />
            </p>
            <h1 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter leading-[0.9]">
              {product.name}
            </h1>
            <div className="mt-4 flex items-center gap-3">
              <Stars value={Math.round(avg)} />
              <span className="text-xs text-gray-500 uppercase tracking-widest">
                {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
              </span>
            </div>
            <p className="mt-6 text-gray-300 leading-relaxed">{product.description}</p>

            <div className="mt-8">
              <BatchModeToggle />
              {mode === "wholesale" && !wholesaleAvailable && (
                <p className="text-xs text-gray-500 mt-2 uppercase tracking-widest">
                  Wholesale not available for this item
                </p>
              )}
            </div>

            {/* Package picker */}
            {packages.length > 0 && (
              <div className={`mt-8 ${soldOut ? "opacity-30 grayscale pointer-events-none select-none" : ""}`} data-testid="package-picker">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
                  Pick a size
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {packages.map((p) => (
                    <button
                      key={p.key}
                      data-testid={`package-${p.key}`}
                      onClick={() => setSelectedKey(p.key)}
                      className={`text-left border p-4 transition-all ${
                        selectedKey === p.key
                          ? "border-[var(--hk-orange)] bg-[var(--hk-orange)]/10"
                          : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-display uppercase text-sm tracking-wider text-white">
                          {p.label}
                        </span>
                        {selectedKey === p.key && (
                          <Check size={14} className="text-[var(--hk-orange)]" />
                        )}
                      </div>
                      <p className="font-display text-xl text-[var(--hk-orange)]">
                        ${p.price.toFixed(2)}
                      </p>
                      {mode === "wholesale" && p.min_qty > 1 && (
                        <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                          Min {p.min_qty}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex items-end justify-between gap-4 border-t border-white/10 pt-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                  {priceContext}
                </p>
                <p className="font-display text-4xl md:text-5xl text-[var(--hk-orange)] mt-1">
                  ${unitPrice?.toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Total</p>
                <p className="font-display text-3xl text-white mt-1">${total.toFixed(2)}</p>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-5">
              <div className="flex items-center gap-3 border border-white/20 px-3 py-2">
                <button
                  data-testid="qty-down"
                  onClick={() => setQty(Math.max(minQty, qty - 1))}
                  className="text-white/70 hover:text-white"
                >
                  <Minus size={14} />
                </button>
                <span className="text-sm w-6 text-center">{qty}</span>
                <button data-testid="qty-up" onClick={() => setQty(qty + 1)} className="text-white/70 hover:text-white">
                  <Plus size={14} />
                </button>
              </div>
              <button
                data-testid="detail-add-to-cart"
                onClick={handleAdd}
                disabled={soldOut || (mode === "wholesale" && !wholesaleAvailable)}
                className="flex-1 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider py-4 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors disabled:opacity-40"
              >
                {soldOut ? "Out of stock" : "Add to bag"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 bg-[var(--hk-surface)] border-t border-white/10">
        <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-8">
          What people <span className="italic text-[var(--hk-orange)]">are saying</span>
        </h2>

        <form onSubmit={submitReview} className="border border-white/10 p-6 mb-10 bg-black/40" data-testid="review-form">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Leave a review</p>
          <div className="flex items-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`rating-${n}`}
                onClick={() => setRating(n)}
                className="transition-transform hover:scale-110"
              >
                <Star size={26} className={n <= rating ? "fill-[var(--hk-orange)] text-[var(--hk-orange)]" : "text-white/30"} />
              </button>
            ))}
          </div>
          <textarea
            data-testid="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={user ? "What do you think of this strain?" : "Sign in to leave a review."}
            rows={3}
            className="w-full bg-transparent border border-white/15 focus:border-[var(--hk-purple)] outline-none p-3 text-white placeholder-gray-600"
          />
          <button
            type="submit"
            data-testid="post-review-btn"
            disabled={posting}
            className="mt-3 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-6 py-2.5 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors disabled:opacity-60"
          >
            {reviewBtnLabel}
          </button>
        </form>

        {reviews.length === 0 ? (
          <p className="text-gray-500 italic">No reviews yet. Be the first.</p>
        ) : (
          <div className="space-y-5">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="border border-white/10 p-5 bg-black/40">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-display uppercase text-white">{r.user_name}</span>
                    <Stars value={r.rating} small />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-gray-600">
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </section>
      <ImageLightbox
        open={zoomOpen}
        src={resolveImageUrl(product.image)}
        alt={product.name}
        onClose={() => setZoomOpen(false)}
      />
    </div>
  );
}

function Stars({ value, small }) {
  const size = small ? 12 : 16;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= value ? "fill-[var(--hk-orange)] text-[var(--hk-orange)]" : "text-white/20"} />
      ))}
    </div>
  );
}
